import { useEffect, useState } from 'react'
import { AlertTriangle, Ban, BellOff, BellRing, Check, Code, Eye, FileCheck, Loader2, Maximize2, RefreshCw, ShieldCheck, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api } from '@/lib/api'
import { useApprovalResults } from '@/hooks/useApi'
import { useApprovalCentre } from '@/hooks/useApprovalCentre'
import type { ApprovalDetail, ApprovalItem } from './ApprovalCentreProvider'

function getExpiresAt(createdAt: string, expiresIn: number): number {
  return new Date(createdAt).getTime() + expiresIn * 1000
}

function timeLeft(createdAt: string, expiresIn: number, now: number): number {
  return Math.max(0, Math.floor((getExpiresAt(createdAt, expiresIn) - now) / 1000))
}

function formatTimeLeft(secs: number): string {
  if (secs <= 0) return 'Expired'
  if (secs < 60) return secs + 's left'
  return Math.ceil(secs / 60) + 'm left'
}

function timeSince(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (secs < 60) return secs + 's ago'
  if (secs < 3600) return Math.floor(secs / 60) + 'm ago'
  return Math.floor(secs / 3600) + 'h ago'
}

function toolLabel(tool: string): string {
  return tool.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

function negativeLabel(item: ApprovalItem): string {
  const value = (item.tool || '').toLowerCase()
  if (/delete|remove|drop|uninstall|purge/.test(value)) return 'Keep'
  if (/access|permission|capability|credential|invite/.test(value)) return 'Deny'
  if (/dismiss|alert/.test(value)) return 'Dismiss'
  return 'Reject'
}

function formatDate(value?: string | null): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

export function ApprovalCentre({ mode = 'all' }: { mode?: 'all' | 'approvals' | 'history' }) {
  const {
    principalId, approvals, pendingCount, isLoading, isFetching, pollError, now, actionToken,
    pushEnabled, pushBusy, refresh, approve, deny, loadReview, togglePush,
  } = useApprovalCentre()
  const history = useApprovalResults()
  const [result, setResult] = useState<{ token: string; success: boolean; message: string; output?: string; fullOutput?: string } | null>(null)
  const [fullOutputModal, setFullOutputModal] = useState<string | null>(null)
  const [reviewModal, setReviewModal] = useState<ApprovalDetail | null>(null)
  const [reviewLoading, setReviewLoading] = useState<string | null>(null)
  const [reviewSource, setReviewSource] = useState<Set<string>>(new Set())
  const [dismissing, setDismissing] = useState<string | null>(null)

  useEffect(() => {
    setReviewModal(null)
    setFullOutputModal(null)
    setResult(null)
    setReviewSource(new Set())
  }, [principalId])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('approval')
    if (!token) return
    let cancelled = false
    setReviewLoading(token)
    loadReview(token).then(detail => {
      if (cancelled) return
      setReviewSource(new Set())
      setReviewModal(detail)
      params.delete('approval')
      const next = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (next ? '?' + next : ''))
    }).catch(err => {
      if (!cancelled) setResult({ token, success: false, message: err instanceof Error ? err.message : 'Could not load approval review' })
    }).finally(() => {
      if (!cancelled) setReviewLoading(null)
    })
    return () => { cancelled = true }
  }, [loadReview])

  async function handleApprove(item: ApprovalItem) {
    setResult(null)
    try {
      const res = await approve(item.token) as { result?: unknown; tool?: string }
      const resStr = JSON.stringify(res?.result || {})
      setResult({
        token: item.token,
        success: true,
        message: toolLabel(res?.tool || item.tool) + ' executed',
        output: resStr.slice(0, 200),
        fullOutput: resStr,
      })
    } catch (err: unknown) {
      setResult({ token: item.token, success: false, message: err instanceof Error ? err.message : 'Execution failed' })
    }
  }

  async function handleDeny(item: ApprovalItem) {
    setResult(null)
    try {
      await deny(item.token)
      setResult({ token: item.token, success: false, message: toolLabel(item.tool) + ' not authorised' })
    } catch (err: unknown) {
      setResult({ token: item.token, success: false, message: err instanceof Error ? err.message : 'Decision failed' })
    }
  }

  async function handleReview(item: ApprovalItem) {
    setReviewLoading(item.token)
    try {
      const detail = await loadReview(item.token)
      setReviewSource(new Set())
      setReviewModal(detail)
    } catch (err: unknown) {
      setResult({ token: item.token, success: false, message: err instanceof Error ? err.message : 'Could not load detail' })
    } finally {
      setReviewLoading(null)
    }
  }

  async function handleDismissAlert(token: string) {
    setDismissing(token)
    try {
      await api.dismissAlert(token)
      await refresh()
    } catch {
      // Preserve the existing best-effort KE alert behaviour during relocation.
    } finally {
      setDismissing(null)
    }
  }

  return (
    <div className="space-y-4" id="approval-centre">
      <Card className={`ops-approval-shell p-4 ${mode === 'history' ? 'hidden' : ''}`}>
        <CardHeader className="ops-approval-header -m-4 mb-4 p-4 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-3 text-[17px]">
              <span className="ops-approval-title-icon"><ShieldCheck className="h-5 w-5" /></span>
              Approval Centre
              {pendingCount > 0 && <Badge variant="warning">{pendingCount} pending</Badge>}
            </CardTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => void togglePush()}
                disabled={pushBusy}
                className="ops-approval-toolbar-button h-9 px-3 rounded-xl text-xs text-text-secondary hover:text-accent disabled:opacity-50 flex items-center gap-1.5 transition-all"
                title={pushEnabled ? 'Phone approval notifications enabled' : 'Enable phone approval notifications'}
              >
                {pushBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : pushEnabled ? <BellRing className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                {pushEnabled ? 'Phone alerts on' : 'Phone alerts off'}
              </button>
              <button
                onClick={() => void refresh()}
                disabled={isFetching}
                className="ops-approval-toolbar-button h-9 px-3 rounded-xl text-xs text-text-secondary hover:text-accent disabled:opacity-50 flex items-center gap-1.5 transition-all"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
          <p className="text-xs text-text-muted mt-1">
            Review the exact AI-proposed action before authorising it. Reviewing never executes an action.
          </p>
        </CardHeader>
        <CardContent>
          {pollError && (
            <div className="mb-3 rounded-lg border border-red-500/20 bg-red-500/10 text-red-400 text-xs p-2 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> {pollError}
            </div>
          )}

          {result && (
            <div className={`mb-3 rounded-lg border p-2 text-xs flex items-start gap-2 ${result.success ? 'border-green-500/20 bg-green-500/10 text-green-400' : 'border-red-500/20 bg-red-500/10 text-red-400'}`}>
              {result.success ? <Check className="h-3.5 w-3.5 mt-0.5" /> : <Ban className="h-3.5 w-3.5 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="font-medium">{result.message}</div>
                {result.output && <div className="mt-1 font-mono text-[10px] opacity-70 break-all line-clamp-3">{result.output}</div>}
              </div>
              {result.fullOutput && <button onClick={() => setFullOutputModal(result.fullOutput!)}><Maximize2 className="h-3 w-3" /></button>}
              <button onClick={() => setResult(null)}><X className="h-3 w-3" /></button>
            </div>
          )}

          <div className="space-y-3">
            {isLoading && <div className="py-8 text-center text-text-muted text-sm"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading approvals…</div>}
            {!isLoading && approvals.length === 0 && (
              <div className="ops-approval-empty py-12 text-center text-text-muted text-sm">
                <span className="ops-approval-title-icon mx-auto mb-3"><ShieldCheck className="h-5 w-5" /></span>
                <div className="font-medium text-text-secondary">No pending approvals</div>
                <div className="text-[11px] mt-1 opacity-70">New governed actions will appear here for review.</div>
              </div>
            )}
            {approvals.map(item => {
              const secsLeft = timeLeft(item.created_at, item.expires_in, now)
              const expired = secsLeft <= 0
              return (
                <div key={item.token} className={`ops-approval-card rounded-2xl p-4 pl-5 ${expired ? 'opacity-50' : ''}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {item.type === 'ke_alert' && <AlertTriangle className="h-4 w-4 text-amber-400" />}
                        <span className="font-semibold text-text-primary">{item.type === 'ke_alert' ? 'Knowledge Alert' : toolLabel(item.tool)}</span>
                        <Badge variant={item.tier >= 3 ? 'danger' : item.tier >= 2 ? 'warning' : 'default'}>Tier {item.tier}</Badge>
                      </div>
                      <div className="text-[11px] text-text-muted mt-1">
                        Requested {timeSince(item.created_at)} · {formatDate(item.created_at)}
                      </div>
                      {item.requested_by && (
                        <div className="text-[11px] text-text-muted mt-0.5">
                          Requested by <span className="text-text-secondary font-mono">{item.requested_by}</span>
                          {item.requested_by_role ? ` · ${item.requested_by_role}` : ''}
                        </div>
                      )}
                    </div>
                    <div className={`text-xs font-medium ${expired ? 'text-red-400' : secsLeft < 120 ? 'text-amber-400' : 'text-text-muted'}`}>
                      {formatTimeLeft(secsLeft)}
                    </div>
                  </div>

                  {item.summary && item.summary !== '{}' && (
                    <div className="ops-approval-summary mt-3 text-[11px] text-text-muted rounded-xl px-3 py-2 font-mono break-all leading-relaxed">
                      {item.summary.slice(0, 500)}
                    </div>
                  )}

                  {item.type === 'ke_alert' ? (
                    <button
                      onClick={() => void handleDismissAlert(item.token)}
                      disabled={dismissing === item.token}
                      className="mt-3 w-full py-2 rounded-lg border border-amber-500/30 text-amber-400 text-xs font-semibold hover:bg-amber-500/10"
                    >
                      {dismissing === item.token ? 'Dismissing…' : 'Dismiss'}
                    </button>
                  ) : !expired ? (
                    <>
                      <button
                        onClick={() => void handleReview(item)}
                        disabled={reviewLoading === item.token}
                        className="ops-approval-review-button mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-text-secondary text-xs font-semibold hover:text-accent hover:translate-y-[-1px] transition-all"
                      >
                        {reviewLoading === item.token ? <><Loader2 className="h-3 w-3 animate-spin" />Loading…</> : <><Eye className="h-3 w-3" />Review</>}
                      </button>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <button
                          onClick={() => void handleApprove(item)}
                          disabled={!!actionToken}
                          className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-accent text-white text-xs font-semibold hover:bg-accent/90 disabled:opacity-50"
                        >
                          {actionToken === item.token ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                          Approve
                        </button>
                        <button
                          onClick={() => void handleDeny(item)}
                          disabled={!!actionToken}
                          className="flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border text-text-secondary text-xs font-semibold hover:text-red-400 hover:border-red-400/30 disabled:opacity-50"
                        >
                          {actionToken === item.token + ':deny' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
                          {negativeLabel(item)}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 text-xs text-red-400 text-center">Expired — request a new approval</div>
                  )}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card className={`ops-decision-shell ${mode === 'approvals' ? 'hidden' : ''}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-[16px]"><span className="ops-icon-tile ops-icon-tile-item"><FileCheck className="h-4 w-4" /></span>Recent Decisions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(history.data?.results || []).slice(0, 12).map(row => (
              <div key={row.token} className="ops-decision-row rounded-xl p-3.5 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-text-primary">{toolLabel(row.tool)}</span>
                  <Badge variant={row.status === 'executed' ? 'success' : row.status === 'denied' ? 'warning' : 'danger'}>{row.status}</Badge>
                </div>
                <div className="mt-1 text-text-muted">
                  Requested {formatDate(row.created_at)}
                  {row.status === 'denied' ? ` · Denied by ${row.approved_by || 'unknown'} at ${formatDate(row.approved_at)}` : ''}
                  {row.status !== 'denied' ? ` · Approved by ${row.approved_by || 'unknown'} at ${formatDate(row.approved_at)}` : ''}
                  {row.executed_at ? ` · Finished ${formatDate(row.executed_at)}` : ''}
                </div>
                {row.error && <div className="mt-1 text-red-400 font-mono break-all">{row.error}</div>}
              </div>
            ))}
            {!history.isLoading && (history.data?.results || []).length === 0 && <div className="py-4 text-center text-text-muted text-sm">No recent decisions</div>}
          </div>
        </CardContent>
      </Card>

      {fullOutputModal && (
        <div className="fixed inset-0 z-[99999] bg-black/60 flex items-center justify-center p-4" onClick={() => setFullOutputModal(null)}>
          <div className="bg-surface border border-border rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border"><span className="text-sm font-semibold">Full Tool Output</span><button onClick={() => setFullOutputModal(null)}><X className="h-4 w-4" /></button></div>
            <pre className="flex-1 overflow-auto p-4 text-[11px] font-mono whitespace-pre-wrap break-all">{fullOutputModal}</pre>
          </div>
        </div>
      )}

      {reviewModal && (
        <div className="fixed inset-0 z-[99999] bg-black/60 flex items-center justify-center p-3" onClick={() => setReviewModal(null)}>
          <div className="bg-surface border border-border rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-elevated">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-accent" />
                <span className="text-sm font-semibold">{toolLabel(reviewModal.tool)}</span>
                <Badge variant={reviewModal.tier >= 3 ? 'danger' : 'warning'}>Tier {reviewModal.tier}</Badge>
              </div>
              <button onClick={() => setReviewModal(null)}><X className="h-4 w-4" /></button>
            </div>
            <div className="px-4 py-2 border-b border-border text-[11px] text-text-muted">
              Requested by {reviewModal.requested_by || 'current user'} · {formatDate(reviewModal.created_at)} · Review only — no action is executed here.
            </div>
            <div className="flex-1 overflow-auto p-4">
              {Object.entries(reviewModal.params).map(([key, value]) => {
                const strVal = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
                const pathVal = typeof reviewModal.params.path === 'string' ? reviewModal.params.path : ''
                const pathLower = pathVal.toLowerCase()
                const isPreviewHtml = key === 'preview_html'
                const isHtml = isPreviewHtml || ((key === 'content' || key === 'html' || key === 'body') && (
                  pathLower.endsWith('.html') || pathLower.endsWith('.htm') ||
                  /^\s*<!DOCTYPE/i.test(strVal) || /^\s*<html/i.test(strVal) ||
                  (/<\/(div|p|span|section|body|head|style|table|form|h[1-6])>/i.test(strVal) && strVal.length > 100)
                ))
                const isPdf = key === 'content' && (pathLower.endsWith('.pdf') || /^data:application\/pdf/i.test(strVal.trim()) || strVal.trim().startsWith('JVBERi'))
                const isImage = /^(https?:\/\/\S+\.(png|jpe?g|gif|svg|webp|bmp|ico)(\?[^\s]*)?)$/i.test(strVal.trim()) || /^data:image\//i.test(strVal.trim())
                const isOfficeDoc = key === 'content' && /\.(docx?|xlsx?|pptx?)$/i.test(pathLower)
                const officeType = isOfficeDoc ? (/\.docx?$/i.test(pathLower) ? 'Word Document' : /\.xlsx?$/i.test(pathLower) ? 'Excel Spreadsheet' : /\.pptx?$/i.test(pathLower) ? 'PowerPoint Presentation' : 'Office Document') : null
                const hasPreview = isHtml || isPdf || isImage
                const showSource = reviewSource.has(key)

                return (
                  <div key={key} className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-semibold text-accent">{key}</span>
                      {officeType && <Badge variant="info">{officeType}</Badge>}
                      {hasPreview && (
                        <button
                          onClick={() => setReviewSource(prev => {
                            const next = new Set(prev)
                            next.has(key) ? next.delete(key) : next.add(key)
                            return next
                          })}
                          className="text-[10px] text-text-muted hover:text-accent flex items-center gap-1"
                        >
                          {showSource ? <><Eye className="h-2.5 w-2.5" />Preview</> : <><Code className="h-2.5 w-2.5" />Source</>}
                        </button>
                      )}
                    </div>
                    {isHtml && !showSource ? (
                      <iframe srcDoc={strVal} sandbox="" className="w-full rounded border border-border bg-white" style={{ minHeight: 400 }} title={`Preview: ${key}`} />
                    ) : isPdf && !showSource ? (() => {
                      try {
                        const raw = strVal.trim()
                        const b64 = raw.startsWith('data:') ? (raw.split(',')[1] || raw) : raw
                        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0))
                        const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
                        return <iframe src={url} className="w-full rounded border border-border" style={{ minHeight: 500 }} title={`PDF: ${key}`} />
                      } catch {
                        return <pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-background rounded px-3 py-2 overflow-auto">{strVal}</pre>
                      }
                    })() : isImage && !showSource ? (
                      <div className="rounded border border-border bg-background p-2"><img src={strVal.trim()} alt={key} className="max-w-full max-h-[70vh] rounded" /></div>
                    ) : isOfficeDoc ? (
                      <div className="rounded-lg border border-border bg-background p-5 text-center">
                        <div className="text-sm font-semibold mb-1">{pathVal.split('/').pop()}</div>
                        <div className="text-[11px] text-text-muted">{officeType}</div>
                        {reviewModal.params.preview_html ? <div className="text-[11px] text-accent mt-2">Rendered preview available above</div> : <div className="text-[11px] text-text-muted mt-2 opacity-60">Preview renders when a document preview is supplied</div>}
                      </div>
                    ) : (
                      <pre className="text-[11px] font-mono text-text-secondary whitespace-pre-wrap break-all bg-background rounded px-3 py-2 overflow-auto leading-relaxed">{strVal}</pre>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
